
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useAdminCheck } from '@/hooks/useAdminCheck';

export const useAdminPrestige = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Utiliser le système de rôles sécurisé
  const { isAdmin } = useAdminCheck();
  const hasPrivileges = isAdmin;

  const activatePrestige = useMutation({
    mutationFn: async ({ 
      serverId, 
      days = 30,
      tier = 'gold' 
    }: { 
      serverId: string; 
      days?: number;
      tier?: string;
    }) => {
      if (!hasPrivileges) {
        throw new Error('Action réservée aux fondateurs et administrateurs');
      }

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + days);

      const { data, error } = await supabase
        .from('servers')
        .update({
          is_sponsored: true,
          sponsor_tier: tier,
          prestige_expires_at: expiresAt.toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', serverId)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "✅ Prestige activé",
        description: "Le serveur a été mis à niveau avec succès",
      });
      queryClient.invalidateQueries({ queryKey: ['user-servers'] });
      queryClient.invalidateQueries({ queryKey: ['servers'] });
    },
    onError: (error: any) => {
      toast({
        title: "❌ Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deactivatePrestige = useMutation({
    mutationFn: async (serverId: string) => {
      if (!hasPrivileges) {
        throw new Error('Action réservée aux fondateurs et administrateurs');
      }

      const { data, error } = await supabase
        .from('servers')
        .update({
          is_sponsored: false,
          sponsor_tier: null,
          custom_color: null,
          prestige_expires_at: null,
          prestige_payment_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', serverId)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "✅ Prestige désactivé",
        description: "Le serveur est redevenu standard",
      });
      queryClient.invalidateQueries({ queryKey: ['user-servers'] });
      queryClient.invalidateQueries({ queryKey: ['servers'] });
    },
    onError: (error: any) => {
      toast({
        title: "❌ Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    isAdmin,
    hasPrivileges,
    activatePrestige,
    deactivatePrestige,
  };
};
